import { addMinutes, differenceInMinutes, format, parseISO } from "date-fns";

/**
 * Formats a date or ISO string into a standard event date string.
 * Example: "2025-10-28 Tue"
 */
export function formatEventDate(date: Date | string): string {
	const d = typeof date === "string" ? parseISO(date) : date;
	return format(d, "yyyy-MM-dd EEE");
}

/**
 * Formats a date or ISO string into a standard event time string.
 * Example: "1:00 PM"
 */
export function formatEventTime(date: Date | string, showAmPm = true): string {
	if (!date) return "N/A";
	const d = typeof date === "string" ? parseISO(date) : date;
	return format(d, showAmPm ? "h:mm aa" : "h:mm");
}

/**
 * Formats a date or ISO string into a 24-hour time string.
 * Example: "13:00"
 */
export function formatTime24(date: Date | string): string {
	if (!date) return "--:--";
	const d = typeof date === "string" ? parseISO(date) : date;
	return format(d, "HH:mm");
}

/**
 * Formats started_at and ended_at into a display string with optional duration.
 * Example: "2025-10-28 Tue・1:00 PM (1 hour)"
 */
export function formatDateTimeWithDuration(
	startedAt: string | Date,
	endedAt?: string | Date,
): string {
	if (!startedAt) return "N/A";

	const start = typeof startedAt === "string" ? parseISO(startedAt) : startedAt;
	const datePart = formatEventDate(start);
	const timePart = formatEventTime(start);

	if (!endedAt) return `${datePart}・${timePart}`;

	const end = typeof endedAt === "string" ? parseISO(endedAt) : endedAt;
	const durationMinutes = differenceInMinutes(end, start);

	let durationStr = "";
	if (durationMinutes >= 60) {
		const hrs = Math.floor(durationMinutes / 60);
		const mins = durationMinutes % 60;
		if (mins > 0) {
			durationStr = ` (${hrs} hr ${mins} min)`;
		} else {
			durationStr = ` (${hrs} hour${hrs > 1 ? "s" : ""})`;
		}
	} else {
		durationStr = ` (${durationMinutes} min)`;
	}

	return `${datePart}・${timePart}${durationStr}`;
}

/**
 * Formats a date for Hero components.
 * Example: "1:00 PM・2025-10-28・Tue"
 */
export function formatHeroDateTime(date: Date | string): string {
	if (!date) return "N/A";
	const d = typeof date === "string" ? parseISO(date) : date;
	return format(d, "h:mm aa・yyyy-MM-dd・EEE");
}

/**
 * Format a date string for display (e.g., "Jan 10, 2025 10:45 AM")
 */
export function formatVideoTimestamp(date: Date | string): string {
	if (!date) return "N/A";
	const d = typeof date === "string" ? parseISO(date) : date;
	return format(d, "MMM d, yyyy h:mm aa");
}

/**
 * Calculate duration between two dates and format as MM:SS
 */
export function formatDurationMMSS(
	start: Date | string,
	end: Date | string,
): string {
	if (!start || !end) return "";
	const s = typeof start === "string" ? parseISO(start) : start;
	const e = typeof end === "string" ? parseISO(end) : end;
	const durationSeconds = Math.floor((e.getTime() - s.getTime()) / 1000);
	if (durationSeconds <= 0) return "";
	const mins = Math.floor(durationSeconds / 60);
	const secs = durationSeconds % 60;
	return `${mins}:${secs.toString().padStart(2, "0")}`;
}

/**
 * Calculates the next possible session start time.
 * Protocol: max(now, latest_session_end, event_start) + 1 minute
 */
export function calculatePossibleStart(
	now: Date,
	latestSessionEnd: Date | null,
	eventStart: Date | null = null,
): Date {
	let base = now;
	if (latestSessionEnd && latestSessionEnd > base) {
		base = latestSessionEnd;
	}
	if (eventStart && eventStart > base) {
		base = eventStart;
	}
	return addMinutes(base, 5);
}

/**
 * Calculates the unified expiry time.
 * Protocol: min(jwt_exp, event_end - 1 minute)
 */
export function calculateUnifiedExpiry(jwtExp: Date, eventEnd: Date): Date {
	const eventExpiry = addMinutes(eventEnd, -1);
	return jwtExp < eventExpiry ? jwtExp : eventExpiry;
}
